import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { VoteStatus } from "types/voteEnum";

import { ValidateButton } from "components/common/ValidateButton";
import { Input } from "components/common/Input";
import { Alert } from "components/common/Alert";
import {
    requestEmailPassword,
    requestEmailPin,
} from "components/helper/callApi";

type Props = {
    onlineCode: string;
    onSubmitSuccess: (email: string) => void;
    onSubmitFail: (errorMessage?: string) => void;
    isPassword: boolean;
};

const EmailForm = ({
    onSubmitSuccess,
    onlineCode,
    onSubmitFail,
    isPassword,
}: Props) => {
    const { t } = useTranslation();
    const emailRef = useRef<HTMLInputElement>(null);

    const [status, setStatus] = useState(VoteStatus.Idle);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!emailRef.current) {
            return;
        }

        setStatus(VoteStatus.PendingValidation);

        const email = emailRef.current.value;

        if (isPassword) {
            try {
                await requestEmailPassword({ email, codeOnline: onlineCode });
                onSubmitSuccess(email);
                setStatus(VoteStatus.Idle);
            } catch (err) {
                onSubmitFail();

                if (typeof err !== "number") {
                    setErrorMessage(`${t("emailError")}`);
                    setStatus(VoteStatus.Error);
                    return;
                }

                const responseStatus = err;

                switch (responseStatus) {
                    case 404:
                        setErrorMessage(`${t("sessionNotFound")}`);
                        break;
                    case 403:
                        setErrorMessage(
                            `${t("passwordEmailErrorRequestedTooRecently")}`,
                        );
                        break;
                    case 409:
                        setErrorMessage(`${t("scenarioIncorrect")}`);
                        break;
                    default:
                        setErrorMessage(`${t("emailError")}`);
                }

                setStatus(VoteStatus.Error);
            }
        } else {
            try {
                await requestEmailPin({ email, codeOnline: onlineCode });
                onSubmitSuccess(email);
                setStatus(VoteStatus.Idle);
            } catch (err) {
                let errorMessage: string = t("emailError");

                const responseStatus = err;
                switch (responseStatus) {
                    case 404:
                        errorMessage = t("sessionNotFound");
                        break;
                    case 403:
                        errorMessage = t("pinEmailErrorRequestedTooRecently");
                        break;
                    case 409:
                        errorMessage = t("scenarioIncorrect");
                        break;
                    default:
                }

                onSubmitFail(errorMessage);

                setStatus(VoteStatus.Error);
            }
        }
    };
    return (
        <form method="post" className="form panel-body" onSubmit={handleSubmit}>
            {"error" === status && errorMessage && (
                <AlertError>{errorMessage}</AlertError>
            )}
            <div className="form-group">
                <StyledInput
                    type="email"
                    className="form-control"
                    name="email"
                    ref={emailRef}
                    autoComplete="off"
                    placeholder={`${t("email")} (*)`}
                    aria-label={t("email")}
                    required
                />
            </div>
            <StyledButton
                type="submit"
                className="btn btn-primary btn-full"
                data-join-in-text={t("joinIn")}
                disabled={VoteStatus.PendingValidation === status}
            >
                {isPassword ? `${t("requestPassword")}` : `${t("requestPin")}`}
            </StyledButton>
        </form>
    );
};

const StyledInput = styled(Input)``;

const StyledButton = styled(ValidateButton)``;

const AlertError = styled(Alert)``;

export default EmailForm;
